import { useEditor, type Editor } from "@tiptap/react"
import { migrateMathStrings } from "@tiptap/extension-mathematics"
import { useEffect, useRef } from "react"
import { MathEditRequest } from "../types"
import { createTiptapExtensions } from "../tiptapExtensions"
import "katex/dist/katex.min.css"
import "katex/contrib/mhchem"

type UseTiptapEditorOptions = {
  content: string
  placeholder?: string
  editable?: boolean
  onChange?: (html: string) => void
  onMathEditRequest?: (request: MathEditRequest) => void
}

export const useTiptapEditor = ({
  content,
  placeholder = "Start writing…",
  editable = true,
  onChange,
  onMathEditRequest,
}: UseTiptapEditorOptions) => {
  const editorRef = useRef<Editor | null>(null)
  const onMathEditRequestRef = useRef(onMathEditRequest)

  useEffect(() => {
    onMathEditRequestRef.current = onMathEditRequest
  }, [onMathEditRequest])

  const editor = useEditor({
    extensions: createTiptapExtensions({
      placeholder,
      onMathEditRequest: (request) => {
        onMathEditRequestRef.current?.(request)
      },
    }),
    content,
    editable,
    immediatelyRender: false,
    shouldRerenderOnTransaction: false,
    onUpdate: ({ editor: currentEditor }) => {
      onChange?.(currentEditor.getHTML())
    },
    onCreate: ({ editor: currentEditor }) => {
      editorRef.current = currentEditor
      migrateMathStrings(currentEditor)
    },
  })

  useEffect(() => {
    editorRef.current = editor
  }, [editor])

  useEffect(() => {
    if (!editor) return

    if (editor.getHTML() === content) return

    editor.commands.setContent(content, { emitUpdate: false })
    migrateMathStrings(editor)
  }, [content, editor])

  useEffect(() => {
    if (!editor) return
    editor.setEditable(editable)
  }, [editable, editor])

  return editor
}
